/**
 * A simple imperative signal for requesting a scroll-into-view on a specific element row.
 * Use `requestScrollToElement(id)` before navigating, then `consumeScrollRequest(id)` in the row
 * component on mount — returns the align (and clears the request) if this row should scroll.
 *
 * When changing element-list tabs, pass `{ suppressTabScrollRestore: true }`:
 * child layout effects (row scroll) run before the parent tab-restore effect, so
 * a separate flag is needed or the restore would undo the scroll. Prefer deferring
 * the scroll with {@link afterNextPaint} instead when the row can wait one frame.
 */

export type ElementScrollAlign = Extract<
  ScrollLogicalPosition,
  'nearest' | 'center'
>;

export type ConsumedScrollRequest = {
  align: ElementScrollAlign;
  /** Open the import file, then park its header under the list chrome. */
  expandAfterScroll: boolean;
};

type ScrollRequestOptions = {
  suppressTabScrollRestore?: boolean;
  /** Defaults to `'nearest'`. */
  align?: ElementScrollAlign;
  /**
   * Import drop/add: open the file, then park its header under the list chrome.
   */
  expandAfterScroll?: boolean;
};

let pendingElementId: string | null = null;
let pendingAlign: ElementScrollAlign = 'nearest';
let pendingExpandAfterScroll = false;
let suppressNextTabScrollRestore = false;
let skipSelectedRowAlign = false;
const savedScrollByTab: Record<string, number> = {};

/**
 * Persist `main` scroll for a list tab. Module-scoped so it survives
 * ElementsView remounts when the page changes.
 */
export const rememberListTabScroll = (tab: string, scrollTop: number): void => {
  savedScrollByTab[tab] = scrollTop;
};

/** Last remembered `main` scroll for {@link tab}, if any. */
export const getRememberedListTabScrolls = (): Readonly<
  Record<string, number>
> => savedScrollByTab;

/**
 * While true, selected rows must not `scrollIntoView` on mount — tab restore
 * already placed `main`, and remounted rows would jump it away.
 */
export const beginSkipSelectedRowAlign = (): void => {
  skipSelectedRowAlign = true;
};

export const shouldSkipSelectedRowAlign = (): boolean => skipSelectedRowAlign;

export const endSkipSelectedRowAlign = (): void => {
  skipSelectedRowAlign = false;
};

export const requestScrollToElement = (
  elementId: string,
  options?: ScrollRequestOptions,
) => {
  pendingElementId = elementId;
  pendingAlign = options?.align ?? 'nearest';
  pendingExpandAfterScroll = options?.expandAfterScroll ?? false;
  if (options?.suppressTabScrollRestore) {
    suppressNextTabScrollRestore = true;
  }
};

/**
 * Consume the one-shot tab-restore suppression set by
 * {@link requestScrollToElement}.
 */
export const consumeTabScrollRestoreSuppression = (): boolean => {
  if (!suppressNextTabScrollRestore) return false;
  suppressNextTabScrollRestore = false;
  return true;
};

/**
 * Consume a pending scroll request for {@link elementId}.
 * Returns the request, or `null` when this row should not scroll.
 */
export const consumeScrollRequest = (
  elementId: string,
): ConsumedScrollRequest | null => {
  if (pendingElementId !== elementId) return null;
  pendingElementId = null;
  const align = pendingAlign;
  const expandAfterScroll = pendingExpandAfterScroll;
  pendingAlign = 'nearest';
  pendingExpandAfterScroll = false;
  return { align, expandAfterScroll };
};
